/**
 * Collective — public follow-through page from the landing-strip
 * "26 AI agents" callout. Deliberately abstract: domain orbs (NOT
 * persona names) + three sovereignty pillars + a soft CTA.
 *
 * The exact persona roster, decision rules, and prompt architecture
 * are trade secrets and intentionally NOT exposed here. This page
 * exists to give a curious visitor a confidence-building landing
 * pad — "yes, the AI exists, yes, it's sovereign, here's the proof
 * surface" — without revealing implementation detail.
 *
 * Visual: hexagonal sector ring with a central "Sovereign Core"
 * anchor, faint connection lines, gentle breathing animation on
 * the orbs. Honours the global theme.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Lock, FileSearch, ArrowRight,
  AlertTriangle, ClipboardCheck, Landmark, Coins, ScrollText, Sparkles,
} from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import PublicNavbar from '@/components/PublicNavbar';

const SECTORS = [
  { id: 'risk',       label: 'Risk',       icon: AlertTriangle, angle: 270 },
  { id: 'audit',      label: 'Audit',      icon: ClipboardCheck, angle: 330 },
  { id: 'governance', label: 'Governance', icon: Landmark,       angle: 30  },
  { id: 'finance',    label: 'Finance',    icon: Coins,          angle: 90  },
  { id: 'compliance', label: 'Compliance', icon: ScrollText,     angle: 150 },
  { id: 'esg',        label: 'ESG',        icon: Sparkles,       angle: 210 },
];

const PILLARS = [
  {
    id: 'own-ai',
    icon: ShieldCheck,
    eyebrow: '[YOUR OWN AI]',
    title: 'In your own boundary.',
    body: 'Models run inside your tenant — your cloud, your keys, your control. Not a shared SaaS endpoint, not a third-party inference farm. The AI works for you, exclusively.',
  },
  {
    id: 'nothing-leaves',
    icon: Lock,
    eyebrow: '[NOTHING LEAVES]',
    title: 'Sovereign by construction.',
    body: 'No outbound calls to public model APIs. No telemetry, no analytics ping, no "anonymous" usage reporting. Every prompt, every artifact, every embedding stays within the perimeter you control.',
  },
  {
    id: 'audit-by-default',
    icon: FileSearch,
    eyebrow: '[AUDIT-BY-DEFAULT]',
    title: 'Every decision is replayable.',
    body: 'Every AI action is logged, signed, and reversible — with the inputs, the reasoning trace, and the resulting decision preserved as evidence. You can ask "why?" a year later and get the same answer.',
  },
];

export default function Collective() {
  const { effective } = useTheme();
  const isDark = effective === 'dark';

  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-[#0A0A0A] text-white' : 'bg-[#F7F5F4] text-slate-900'}`}
      data-testid="collective-page"
    >
      <PublicNavbar />

      {/* ── HERO ── */}
      <section className="relative ve-container pt-16 md:pt-24 pb-12">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} style={{ color: '#D4AF37' }} />
            <span
              className="text-[10px] uppercase tracking-[0.32em] font-bold"
              style={{ color: '#D4AF37' }}
            >
              [SOVEREIGN AI · COLLECTIVE]
            </span>
          </div>
          <h1
            className={`display text-4xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Your AI.{' '}
            <span className="italic" style={{ color: 'var(--ve-violet-bright)' }}>
              Your boundary.
            </span>
          </h1>
          <p
            className={`text-lg md:text-xl mt-6 max-w-3xl leading-relaxed ${
              isDark ? 'text-white/70' : 'text-slate-700'
            }`}
          >
            Specialised agents reason across every governance domain — risk,
            audit, finance, compliance, ESG. They work as a single council,
            run entirely inside your sovereign boundary, and never speak to
            anyone else.
          </p>
        </div>
      </section>

      {/* ── SECTOR RING ── */}
      <section className="relative ve-container pb-12 md:pb-20">
        <SectorRing isDark={isDark} />
      </section>

      {/* ── SOVEREIGNTY PILLARS ── */}
      <section className="relative ve-container pb-20 md:pb-28">
        <div className="max-w-3xl">
          <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>
            How it stays sovereign
          </div>
          <h2
            className={`display text-3xl md:text-5xl mt-2 leading-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Three guarantees. No exceptions.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.id}
              data-testid={`pillar-${p.id}`}
              className={`rounded-lg border p-7 transition-all ${
                isDark
                  ? 'border-white/10 bg-white/[0.03] hover:border-[var(--ve-violet-bright)]/40'
                  : 'border-slate-200 bg-white hover:border-[var(--ve-violet)]/30'
              }`}
            >
              <div
                className="h-11 w-11 rounded-md flex items-center justify-center border"
                style={{
                  backgroundColor: 'rgba(174,82,234,0.10)',
                  color: 'var(--ve-violet-bright)',
                  borderColor: 'rgba(174,82,234,0.30)',
                }}
              >
                <p.icon size={20} strokeWidth={1.5} />
              </div>
              <div
                className="mt-5 text-[10px] uppercase tracking-[0.28em] font-bold"
                style={{ color: '#D4AF37' }}
              >
                {p.eyebrow}
              </div>
              <h3
                className={`display text-2xl mt-2 leading-tight ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
              >
                {p.title}
              </h3>
              <p
                className={`mt-3 text-sm leading-relaxed ${
                  isDark ? 'text-white/65' : 'text-slate-600'
                }`}
              >
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SOFT CTA ── */}
      <section className="ve-container pb-24">
        <div
          className={`rounded-lg border p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 ${
            isDark
              ? 'border-white/10 bg-white/[0.02]'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div className="max-w-xl">
            <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>
              See the council at work
            </div>
            <h3
              className={`display text-2xl md:text-3xl mt-2 leading-tight ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              The shortest path is a tailored walkthrough.
            </h3>
            <p
              className={`mt-3 text-sm ${
                isDark ? 'text-white/65' : 'text-slate-600'
              }`}
            >
              Our team maps the council to your governance, risk, and venture
              priorities — and shows how the boundary works in practice.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="collective-cta-pricing"
            className="ve-btn-primary shrink-0"
          >
            Book a demo <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────── Sector Ring ───────────────────────
//
// 6 sector orbs arranged in a hex around a central Sovereign Core
// anchor. Faint connection lines render the council topology
// without revealing persona names or roles.
function SectorRing({ isDark }) {
  // Container dimensions — sized so the longest labels ("GOVERNANCE",
  // "COMPLIANCE") fit comfortably inside their orbs at typography
  // matching the rest of the site (Georgia-serif chrome, mono labels).
  const SIZE = 540;
  const RADIUS = 210;
  const ORB_SIZE = 116;
  const CORE_SIZE = 140;
  const CENTER = SIZE / 2;

  // Build positions from each sector's angle (0° = right, 90° = down).
  const positioned = SECTORS.map((s) => {
    const rad = (s.angle * Math.PI) / 180;
    return {
      ...s,
      x: CENTER + RADIUS * Math.cos(rad),
      y: CENTER + RADIUS * Math.sin(rad),
    };
  });

  const lineStroke = isDark
    ? 'rgba(174,82,234,0.22)'
    : 'rgba(94,31,139,0.22)';
  const centerGlow = isDark
    ? 'radial-gradient(circle, rgba(174,82,234,0.34) 0%, rgba(212,175,55,0.10) 40%, transparent 70%)'
    : 'radial-gradient(circle, rgba(174,82,234,0.20) 0%, rgba(212,175,55,0.08) 40%, transparent 70%)';

  return (
    <div className="w-full flex justify-center" data-testid="sector-ring">
      <div
        className="relative"
        style={{
          width: SIZE,
          maxWidth: '100%',
          aspectRatio: '1 / 1',
        }}
      >
        {/* Connection lines (SVG) */}
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="absolute inset-0 w-full h-full"
          aria-hidden
        >
          {positioned.map((s) => (
            <line
              key={`line-${s.id}`}
              x1={CENTER}
              y1={CENTER}
              x2={s.x}
              y2={s.y}
              stroke={lineStroke}
              strokeWidth="1.25"
              strokeDasharray="2 5"
            />
          ))}
        </svg>

        {/* Central Core */}
        <div
          className="absolute"
          style={{
            left: `${(CENTER / SIZE) * 100}%`,
            top: `${(CENTER / SIZE) * 100}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div
            className={`rounded-full flex items-center justify-center border-2 ${
              isDark ? 'bg-[#0F0F0F]' : 'bg-white'
            }`}
            style={{
              width: CORE_SIZE,
              height: CORE_SIZE,
              background: centerGlow,
              borderColor: 'rgba(174,82,234,0.55)',
              boxShadow: isDark
                ? '0 0 40px -10px rgba(174,82,234,0.5), inset 0 1px 0 rgba(255,255,255,0.04)'
                : '0 18px 40px -16px rgba(94,31,139,0.30)',
            }}
            data-testid="sector-core"
          >
            <div className="text-center px-2">
              <div
                className="text-[9px] uppercase tracking-[0.24em] font-bold font-mono"
                style={{ color: '#D4AF37' }}
              >
                Sovereign
              </div>
              <div
                className={`text-base font-bold leading-tight mt-1 ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                Core
              </div>
              <div
                className={`text-[9px] uppercase tracking-[0.2em] mt-1 font-mono ${
                  isDark ? 'text-white/55' : 'text-slate-500'
                }`}
              >
                Your tenant
              </div>
            </div>
          </div>
        </div>

        {/* Sector orbs */}
        {positioned.map((s, idx) => (
          <SectorOrb
            key={s.id}
            sector={s}
            size={SIZE}
            orbSize={ORB_SIZE}
            isDark={isDark}
            delay={idx * 0.4}
          />
        ))}
      </div>
    </div>
  );
}

function SectorOrb({ sector, size, orbSize, isDark, delay }) {
  const Icon = sector.icon;
  return (
    <div
      data-testid={`sector-orb-${sector.id}`}
      className="absolute group"
      style={{
        left: `${(sector.x / size) * 100}%`,
        top: `${(sector.y / size) * 100}%`,
        transform: 'translate(-50%, -50%)',
        animation: `ve-orb-breathe 6s ease-in-out infinite`,
        animationDelay: `${delay}s`,
      }}
    >
      <style>{`
        @keyframes ve-orb-breathe {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.05); }
        }
      `}</style>
      <div
        className="rounded-full flex items-center justify-center border transition-all duration-300 group-hover:scale-110"
        style={{
          width: orbSize,
          height: orbSize,
          backgroundColor: isDark
            ? 'rgba(15,15,15,0.94)'
            : 'rgba(255,255,255,0.98)',
          borderColor: isDark
            ? 'rgba(174,82,234,0.55)'
            : 'rgba(174,82,234,0.45)',
          boxShadow: isDark
            ? '0 10px 30px -10px rgba(174,82,234,0.55), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 12px 26px -10px rgba(94,31,139,0.20), 0 2px 6px -2px rgba(94,31,139,0.10)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div className="text-center px-2 leading-none">
          <Icon
            size={22}
            strokeWidth={1.5}
            style={{ color: 'var(--ve-violet-bright)' }}
            className="mx-auto"
          />
          <div
            className={`mt-2 text-[10px] uppercase tracking-[0.14em] font-bold font-mono whitespace-nowrap ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {sector.label}
          </div>
        </div>
      </div>
    </div>
  );
}
